import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import DischargeConfirm from 'components/dischargeForm/DischargeConfirm';
import DischargeFileRequests from 'components/dischargeForm/DischargeFileRequests';
import DischargeInformation, {
  DischargeFormMeta,
  DischargeFormValues,
} from 'components/dischargeForm/DischargeInformation';
import DischargeQuestions, { DischargeQuestion } from 'components/dischargeForm/DischargeQuestions';
import {
  buildDischargeFormSteps,
  buildLinkedDischargeFormSteps,
  DischargeSteps,
  isQuestionOptional,
} from 'components/dischargeForm/helpers';
import LinkedDischargeQuestions from 'components/dischargeForm/LinkedDischargeQuestions';
import PccDischargeDetails, { PccDischargeBanner } from 'components/dischargeForm/PccDischargeDetails';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import { PatientName } from 'components/shared/StandardModal';
import { Flags } from 'constants/flags';
import { useForm } from 'context/form';
import { useProfile } from 'context/profile';
import { clientHasIntegration } from 'lib/clientIntegration';
import { DocumentTypes } from 'models/Attachment';
import { ProviderGroupTypeConfig } from 'models/GroupType';
import LocationEpisode from 'models/LocationEpisode';
import LocationEpisodeDischarge from 'models/LocationEpisodeDischarge';
import { attachmentsQueryKeys, indexAttachments } from 'services/api/attachments';

type Props = {
  patientName: string;
  locationEpisode: LocationEpisode;
  linkedLocationEpisodes?: LocationEpisode[];
  onCancel: () => void;
  dischargeQuestionsFlagEnabled: boolean;
  showActualDischargeDate?: boolean;
  onSubtitleChange?: (subtitle: string | undefined) => void;
  locationEpisodeDischarge?: LocationEpisodeDischarge;
};

const REQUESTED_DOCS = [DocumentTypes.DischargeSummary];

export default function DischargeForm({
  locationEpisode,
  linkedLocationEpisodes = [],
  patientName,
  onCancel,
  dischargeQuestionsFlagEnabled,
  showActualDischargeDate = false,
  onSubtitleChange,
  locationEpisodeDischarge,
}: Props) {
  const hasLinkedLEs = linkedLocationEpisodes.length > 0;
  const pccDischargeReport = locationEpisodeDischarge?.pccDischargeReport;
  const { profile } = useProfile();
  // Whether the user is a manager (or affiliate, i.e. view-only manager) for this patient.
  // These users should not see discharge questions — DQ are surveys configured by the
  // owning client for providers to answer at discharge. Affiliate users also never have
  // provider group access, so they are naturally excluded from linked records upstream.
  const isManagerForPatient = useMemo(
    () => !!profile?.canAccessGroup(locationEpisode.owner.id) || !!profile?.isAffiliateUser,
    [profile, locationEpisode.owner.id]
  );

  // Linked LEs where the user is not a manager for that LE's owner.
  // Users should never see discharge questions for records owned by their own customer,
  // but should see them for records owned by other customers (e.g., SNF building user
  // discharging from SNF tab should see HS client's discharge questions).
  const linkedLEsWithDQ = useMemo(
    () =>
      linkedLocationEpisodes.filter(
        (le) =>
          !le.discharged &&
          !profile?.canAccessGroup(le.owner?.id) &&
          le.owner?.client?.hasFlag(Flags.DischargeQuestions) &&
          !!le.dischargeTemplate?.questions?.length
      ),
    [linkedLocationEpisodes, profile]
  );

  const [step, setStep] = useState<string>(DischargeSteps.Information);

  // Integration auto-pulls a discharge CCD onto the LE when the EHR signals
  // discharge (see olio-integration's ccd_discharge_retrieval). Look for it
  // here so we can attach it to the discharge note (and skip the Discharge
  // Summary upload step) by injecting it into the form's attachment list.
  const ccdLookupEnabled =
    clientHasIntegration(locationEpisode.owner.client) && locationEpisode.patient.hasIntegrationExternalId;

  const ccdQueryParams = {
    locationEpisodeId: locationEpisode.id,
    docType: DocumentTypes.CCD,
    archived: false,
    pageSize: 1,
    // The api gates Attachment#filename behind `join_included? :blob`.
    include: 'blob',
  };

  const { data: ccdAttachmentsPage } = useQuery({
    queryKey: attachmentsQueryKeys.index(ccdQueryParams),
    queryFn: ({ signal }) => indexAttachments(ccdQueryParams, signal),
    enabled: ccdLookupEnabled,
  });
  const ccdAttachment = ccdAttachmentsPage?.data?.[0];

  const groupTypeConfig = (locationEpisode.group.groupType.config ?? {}) as ProviderGroupTypeConfig;

  const { valid, dirty, values, submitForm, meta } = useForm<DischargeFormValues, DischargeFormMeta>();

  // Whether the CCD is currently attached to the discharge note. Reflects the
  // user's interaction with the attachment chip — flips false when they trash
  // it. `DischargeInformation` merges the auto-pulled CCD into the note's
  // attachments on first arrival; this derivation just reads back the state.
  const hasKeptIntegrationCcd =
    ccdLookupEnabled && !!ccdAttachment && (values.note?.attachments ?? []).some((a) => a.id === ccdAttachment.id);

  const questions = useMemo(
    () => (dischargeQuestionsFlagEnabled ? (locationEpisode.dischargeTemplate?.questions ?? []) : []),
    [dischargeQuestionsFlagEnabled, locationEpisode.dischargeTemplate?.questions]
  );

  // Whether the primary LE has discharge questions to show.
  const primaryHasDischargeQuestions = !!questions.length && !isManagerForPatient;

  const canContinueCare =
    !!values.dischargeReason?.dischargedToGroupType?.isProvider && values.dischargedGroup?.name !== 'Other';
  const hasAllRequestedFiles = REQUESTED_DOCS.every((doc) =>
    (values.note?.attachments || []).some((a) => a.docType === doc)
  );

  // Build the list of LEs that should have DQ steps.
  // Primary is included only if the user is not a manager for it.
  // Linked LEs are already filtered upstream (manager-only and affiliate users
  // receive an empty linkedLocationEpisodes from DischargeModal).
  const dischargeQuestionLEs = useMemo(
    () => [...(primaryHasDischargeQuestions ? [locationEpisode] : []), ...linkedLEsWithDQ],
    [primaryHasDischargeQuestions, locationEpisode, linkedLEsWithDQ]
  );

  const steps = useMemo(
    () =>
      hasLinkedLEs
        ? buildLinkedDischargeFormSteps({
            dischargeQuestionLEs,
            canContinueCare,
            hasAllRequestedFiles,
            hideDischargeSummaryPrompt: groupTypeConfig.hideDischargeSummaryPrompt,
            hasKeptIntegrationCcd,
            currentStep: step,
          })
        : buildDischargeFormSteps({
            hasDischargeQuestions: primaryHasDischargeQuestions,
            canContinueCare,
            hasAllRequestedFiles,
            hideDischargeSummaryPrompt: groupTypeConfig.hideDischargeSummaryPrompt,
            hasKeptIntegrationCcd,
            currentStep: step,
          }),
    [
      step,
      hasLinkedLEs,
      dischargeQuestionLEs,
      primaryHasDischargeQuestions,
      canContinueCare,
      hasAllRequestedFiles,
      groupTypeConfig.hideDischargeSummaryPrompt,
      hasKeptIntegrationCcd,
    ]
  );

  const hasMoreSteps = useMemo(() => steps.indexOf(step) < steps.length - 1, [step, steps]);

  const onPreviousStep = () => {
    if (step === DischargeSteps.Information) {
      onCancel();
      return;
    }

    const currentIndex = steps.indexOf(step);
    setStep(steps[currentIndex - 1]);
  };

  const onNextStep = () => {
    if (!hasMoreSteps) {
      submitForm();
      return;
    }

    const currentIndex = steps.indexOf(step);
    setStep(steps[currentIndex + 1]);
  };

  const dischargeQuestions: DischargeQuestion[] = useMemo(() => {
    return questions.map((q) => (isQuestionOptional(q, questions, values.questions) ? { ...q, optional: true } : q));
  }, [questions, values.questions]);

  const allRequiredQuestionsAnswered = useMemo(() => {
    return dischargeQuestions.every((q) => q.optional || values.questions?.[q.id]);
  }, [dischargeQuestions, values.questions]);

  const currentStepQuestionsAnswered = useMemo(() => {
    if (!step.startsWith('questions_')) return true;
    const leId = step.replace('questions_', '');
    const allLEs = [locationEpisode, ...linkedLocationEpisodes];
    const targetLE = allLEs.find((le) => le.id === leId);
    if (!targetLE) return true;

    const stepQuestions = targetLE.dischargeTemplate?.questions ?? [];
    const isPrimary = targetLE.id === locationEpisode.id;
    const answers = isPrimary ? values.questions : values.linkedQuestions?.[leId];

    return stepQuestions.every((q) => isQuestionOptional(q, stepQuestions, answers) || answers?.[q.id]);
  }, [step, locationEpisode, linkedLocationEpisodes, values.questions, values.linkedQuestions]);

  const questionSteps = useMemo(() => steps.filter((s) => s.startsWith('questions_')), [steps]);

  useEffect(() => {
    if (!step.startsWith('questions_') || questionSteps.length <= 1) {
      onSubtitleChange?.(undefined);
      return;
    }
    const leId = step.replace('questions_', '');
    const allLEs = [locationEpisode, ...linkedLocationEpisodes];
    const targetLE = allLEs.find((le) => le.id === leId);
    if (!targetLE) {
      onSubtitleChange?.(undefined);
      return;
    }
    const stepIndex = questionSteps.indexOf(step);
    const clientName = targetLE.owner?.client?.name ?? targetLE.group?.name;
    onSubtitleChange?.(`Record ${stepIndex + 1} of ${questionSteps.length}: ${clientName}`);
  }, [step, questionSteps, locationEpisode, linkedLocationEpisodes, onSubtitleChange]);

  return (
    <>
      <PatientName>Patient: {patientName}</PatientName>

      {step === DischargeSteps.Information && pccDischargeReport && locationEpisodeDischarge && (
        <PccDischargeBanner report={pccDischargeReport} locationEpisodeDischarge={locationEpisodeDischarge} />
      )}
      {step === DischargeSteps.Information && (
        <DischargeInformation
          locationEpisode={locationEpisode}
          showActualDischargeDate={showActualDischargeDate}
          hideDischargedAma={groupTypeConfig.hideDischargedAma}
          taggable={!hasLinkedLEs}
          initialAttachments={ccdAttachment ? [ccdAttachment] : undefined}
        />
      )}
      {step === DischargeSteps.Information && pccDischargeReport && <PccDischargeDetails report={pccDischargeReport} />}
      {step === DischargeSteps.FileRequests && <DischargeFileRequests />}
      {!hasLinkedLEs && step === DischargeSteps.Questions && <DischargeQuestions questions={dischargeQuestions} />}
      {hasLinkedLEs && step.startsWith('questions_') && (
        <LinkedDischargeQuestions step={step} primaryLE={locationEpisode} linkedLEs={linkedLocationEpisodes} />
      )}
      {step === DischargeSteps.Confirm && <DischargeConfirm />}

      <Actions>
        <Button variant='ghost' type='button' onClick={onPreviousStep}>
          {step === DischargeSteps.Information ? 'Cancel' : 'Back'}
        </Button>
        <Button
          type='button'
          disabled={
            !valid ||
            !dirty ||
            meta.isUploadingAttachments ||
            (step === DischargeSteps.Questions && !allRequiredQuestionsAnswered) ||
            (step.startsWith('questions_') && !currentStepQuestionsAnswered)
          }
          loading={meta.isSubmitting}
          onClick={onNextStep}>
          {hasMoreSteps ? 'Next' : 'Confirm'}
        </Button>
      </Actions>
    </>
  );
}

const Actions = styled(ButtonGroup)`
  margin-top: 24px;
  justify-content: flex-end;
`;
